import { z } from "zod";

import {
  AnalyticsViewTypeList,
  DeviceTypeList,
} from "../../schemas/analytics/base";
import { DatasetSchema } from "../../schemas/analytics/metrics";
import {
  ExperienceExclusionType,
  PluginEntityType,
  PluginProductConfigType,
} from "../../schemas/plugin";
import { PluginTrafficConfigType_2023_0_20 } from "../../schemas/plugin/config/experiments";
import { WidgetTypeType } from "../../schemas/widgets";
import { EstimatedTestLengthRangeSchema } from "../experience-analysis";
import {
  ExperienceIntegrationDtoSchema,
  UnknownExperienceIntegrationDto,
} from "../experience-integrations";
import { ExperienceProductDtoSchema } from "../experience-products";
import {
  ExperienceCurrencyDto,
  ExperienceCurrencyDtoSchema,
  ExperienceTypeList,
} from "../experiences";
import { S3FileLocationDtoSchema } from "../s3-file-location";
import { ShippingTestMethodDefinitionDtoSchema } from "../shipping-test-method-definitions";
import { UserInterfaceDtoSchema } from "../user-interfaces";

import { DiscountLikeObject } from "./discounts";
import {
  CreateTestGroupDtoSchema,
  RequiredTestGroupSchema,
  TestGroupDto,
  TestGroupDtoSchema,
  TestGroupLikeObject,
  TestGroupSchema,
} from "./test-groups";
import { TrafficConfigDtoSchema } from "./traffic-configs";

export const ExperimentTestTypeList = [
  "pricing",
  "shipping",
  "campaign",
  "content",
] as const;
export type ExperimentTestType = (typeof ExperimentTestTypeList)[number];

export const ExperimentAnalyticStatusList = [
  "Gathering Data",
  "Ready for Analysis",
  "Complete",
] as const;
export type ExperimentAnalyticsStatus =
  (typeof ExperimentAnalyticStatusList)[number];

const GoogleAnalyticsSchema = z.object({
  customDimensionIndex: z.number().nullish(),
});
export type GoogleAnalyticsType = z.infer<typeof GoogleAnalyticsSchema>;

export const ExperimentDevicePreviewDtoSchema = z.object({
  device: z.enum(DeviceTypeList).default("desktop"),
  path: z.string().default("/"),
});

export type ExperimentDevicePreviewDto = z.infer<
  typeof ExperimentDevicePreviewDtoSchema
>;

export const RequiredExperimentSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string(),
  hasTestPricing: z.boolean().default(true),
  hasTestShipping: z.boolean().default(false),
  hasTestCampaign: z.boolean().default(false),
  hasTestContent: z.boolean().default(false),
  isPreview: z.boolean().optional().default(true),
  description: z.string().nullish(),
  createdAtTs: z.number().optional(),
  startedAtTs: z.number().nullish(),
  pausedAtTs: z.number().nullish(),
  endedAtTs: z.number().nullish(),
  status: z.string().nullish(),
  progress: z.number().nullish(),
  analysisUrl: z.string().nullish(),
  controlMonthly: z.number().nullish(),
  excludeFromImpact: z.boolean().default(false).optional(),
  testGroups: RequiredTestGroupSchema.array(),
  impact: z.number().nullish(),
  analyticsViewType: z.enum(AnalyticsViewTypeList).default("none").optional(),
  estimatedTestLengthRange: EstimatedTestLengthRangeSchema.default({
    low: null,
    high: null,
  }).optional(),
  impactMetric: z.string().nullish(),
  devicePreview: ExperimentDevicePreviewDtoSchema.nullish(),
  previewPath: z.string().nullish(),
  visitors: z.number().optional(),
  orders: z.number().optional(),
  testFlowType: z.enum(ExperienceTypeList),
  goal: z.string().nullish(),
  isExternal: z.boolean().default(false).nullish(),
  currency: ExperienceCurrencyDtoSchema.optional().nullish(),
});
export type RequiredExperimentType = z.infer<typeof RequiredExperimentSchema>;

export const ExperimentDtoSchema = RequiredExperimentSchema.extend({
  testGroups: TestGroupSchema.array(), // required ID
  testProducts: ExperienceProductDtoSchema.array().default([]).optional(),
  testProductsLocation: S3FileLocationDtoSchema.nullish(),
  trafficConfig: TrafficConfigDtoSchema.nullish(),
  containsProductErrors: z.boolean(),
  ga: GoogleAnalyticsSchema.nullish(),
  metrics: z.any().nullish(),
  volumeDiscountControlId: z.string().uuid().nullish(),
  randomSeed: z.string().nullish(),
  controlGroup: TestGroupSchema.nullish(),
  totalTestProducts: z.number().nullish(),
  experimentIntegrations: z.optional(ExperienceIntegrationDtoSchema.array()),
  shippingTestMethodDefinitions: z.optional(
    ShippingTestMethodDefinitionDtoSchema.array()
  ),
  allTestProductsLoaded: z.boolean().optional(),
  currency: ExperienceCurrencyDtoSchema.optional().nullish(),
});

type _ExperimentType = z.infer<typeof ExperimentDtoSchema>;

export interface ExperimentDto extends RequiredExperimentType, _ExperimentType {
  testGroups: TestGroupDto[];
}

export const EnsuredExperimentStateSchema = ExperimentDtoSchema.extend({
  testGroups: TestGroupDtoSchema.array(),
  trafficConfig: TrafficConfigDtoSchema,
  testProducts: ExperienceProductDtoSchema.array().default([]),
  devicePreview: ExperimentDevicePreviewDtoSchema,
  allTestProductsLoaded: z.boolean().default(false),
});

type _EnsuredExperimentStateType = z.infer<typeof EnsuredExperimentStateSchema>;

export const ExperimentStateWithTrafficConfigs = ExperimentDtoSchema.extend({
  trafficConfig: TrafficConfigDtoSchema,
});
export type ExperimentStateWithTrafficConfigType = z.infer<
  typeof ExperimentStateWithTrafficConfigs
>;

export const ExperimentWithIdSchema = ExperimentDtoSchema.extend({
  id: z.string().uuid(),
  testGroups: TestGroupSchema.array(),
  trafficConfig: TrafficConfigDtoSchema.nullish(),
  datasets: DatasetSchema.nullish(),
  userInterfaces: UserInterfaceDtoSchema.array().optional().default([]),
});
type _ExperimentWithIdType = z.infer<typeof ExperimentWithIdSchema>;

export type ExperimentWithIdType = ExperimentDto & _ExperimentWithIdType;

export const CreateExperimentDtoSchema = ExperimentDtoSchema.omit({
  excludeFromImpact: true,
  isPreview: true,
  hasTestPricing: true,
  hasTestShipping: true,
  hasTestCampaign: true,
  hasTestContent: true,
  containsProductErrors: true,
}).extend({
  testGroups: CreateTestGroupDtoSchema.array(),
  testProducts: ExperienceProductDtoSchema.array()
    .default([])
    .optional()
    .or(z.null()),
  testProductsLocation: S3FileLocationDtoSchema.nullish(),
});

export type CreateExperimentDto = z.infer<typeof CreateExperimentDtoSchema>;

export const ProgressBarConfigSchema = z.object({
  subtotalQuerySelector: z.string(),
  defaultThreshold: z.string(),
  minimumPurchaseAmount: z.string(),
  underMinimumText: z.string(),
  underThresholdText: z.string(),
  overThresholdText: z.string(),
  maxWidth: z.string(),
  textColor: z.string(),
  textSize: z.string(),
  underMinimumBackgroundColor: z.string(),
  progressBarBackgroundColor: z.string(),
  progressBarCompletedColor: z.string(),
  progressBarHeight: z.string(),
  showWhileCartIsEmpty: z.boolean().optional(),
  enabled: z.boolean().optional(),
});
export type ProgressBarConfigType = z.infer<typeof ProgressBarConfigSchema>;

export interface ExperimentLikeObject {
  id: string;
  name: string;
  type: PluginEntityType;
  isPreview: boolean;
  pausedAtTs: number | null;
  hasBannerConfig: boolean;
  hasTestPricing: boolean;
  hasTestCampaign: boolean;
  hasTestShipping: boolean;
  isDiscountEnabled: boolean;
  forceExcludeEntireExperiment: boolean;
  currency?: ExperienceCurrencyDto;
  trafficConfig?: PluginTrafficConfigType_2023_0_20;
  testProducts: PluginProductConfigType[] | null;
  testGroups: TestGroupLikeObject[] | null;
  controlGroup: TestGroupLikeObject | null;
  measurementId: string | null;
  experimentIntegrations: UnknownExperienceIntegrationDto[] | null;
  getTestGroup: () => TestGroupLikeObject | null;

  getProductIdFromVariantId(variantId: string): string | null;

  determinePageTargetingIncluded: (id: string) => boolean;
  determineGlobalTrafficConfigExclusions: (options?: any) => {
    trafficExclusion: false | ExperienceExclusionType;
    newExclusions?: string[];
  };
  getWidget: <T>(widgetType: WidgetTypeType) => T | undefined;
  hasWidget: (widgetType: WidgetTypeType) => boolean;
  getProgressBarBuildRequirements: () => {
    discount: DiscountLikeObject;
  } | null;
}

export interface SitewideLikeObject {
  id: string;
  type: PluginEntityType;
  getWidget: <T>(widgetType: WidgetTypeType) => T | undefined;
  hasWidget: (widgetType: WidgetTypeType) => boolean;
  getProgressBarBuildRequirements: () => {
    discount: DiscountLikeObject;
  } | null;
}
